import { API_POST, DBRequest } from '../../api/api';
import * as Sentry from '@sentry/browser'; // or '@sentry/react'

class PushError extends Error {
  constructor(message, details) {
    super(message);
    this.details = details;
  }
}

/*
//Function which allows to test timeouts (!DO NOT USE IN PRODUCTION)
function forever() {
  return new Promise(() => {});
}
// Usage await forever();
//*/

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function withTimeout(promise, ms, error = new Error('Timeout')) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(error), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export async function subscribeForPush(config, publicVapidKey, alreadyGranted) {
  // 1. Ask for permission
  if (!alreadyGranted) {
    if (!('Notification' in window) || Notification?.permission === 'denied') {
      return 'denied';
    }
    let permission;
    try {
      permission = await withTimeout(
        Notification?.requestPermission(),
        30000,
        new PushError('Permission check timeout', {
          Notification,
          publicVapidKey,
          alreadyGranted,
          permission,
        }),
      );
    } catch (err) {
      console.error(err);
      Sentry.captureException(err, { extra: { details: err.details } });
      return 'error';
    }

    if (permission !== 'granted') return 'denied';
  }

  if (!publicVapidKey) return 'error';

  // 2. Get or register service worker
  let registration;
  try {
    registration = await withTimeout(
      navigator.serviceWorker
        .getRegistration('/service-worker.js')
        .then(
          (existing) =>
            existing || navigator.serviceWorker.register('/service-worker.js'),
        ),
      10000,
      new PushError('Service worker registration timeout', {
        publicVapidKey,
        alreadyGranted,
      }),
    );
  } catch (err) {
    console.error(err);
    Sentry.captureException(err, { extra: { details: err.details } });
    return 'error';
  }

  // Wait for the worker to be ready only if not already active
  if (!registration.active) {
    try {
      registration = await withTimeout(
        navigator.serviceWorker.ready,
        10000,
        new PushError('Service worker not ready in time', {
          publicVapidKey,
          alreadyGranted,
        }),
      );
    } catch (err) {
      console.error(err);
      Sentry.captureException(err, { extra: { details: err.details } });
      return 'error';
    }
  }

  // 3. Reuse existing subscription or create a new one
  let subscription;
  try {
    subscription = await withTimeout(
      registration.pushManager.getSubscription(),
      5000,
      new PushError('Get existing subscription timeout'),
    );
  } catch (err) {
    // Non-fatal — fall through to create a new subscription
    subscription = null;
  }

  if (!subscription) {
    try {
      subscription = await withTimeout(
        registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicVapidKey),
        }),
        10000,
        new PushError('Push subscription timeout', {
          publicVapidKey,
          alreadyGranted,
        }),
      );
    } catch (err) {
      console.error(err);
      Sentry.captureException(err, { extra: { details: err.details } });
      return 'error';
    }
  }

  const supportedEncodings = PushManager.supportedContentEncodings;
  const usedEconding = supportedEncodings[supportedEncodings.length - 1];

  return new Promise((resolve, reject) => {
    DBRequest({
      config,
      path: 'web-push/subscribe',
      method: API_POST({
        ...subscription.toJSON(),
        contentEncoding: usedEconding,
      }),
      onResponse: (res) => {
        resolve('success');
      },
      onError: (error) => {
        reject('error');
      },
    });
  });
}

export async function unsubscribeFromPush(config) {
  // 1. Get the service worker registration
  const registration = await navigator?.serviceWorker?.ready;

  // 2. Get the existing subscription
  const subscription = await registration?.pushManager?.getSubscription();

  if (!subscription) {
    return 'no-subscription';
  }

  // 3. Unsubscribe in the browser
  await subscription?.unsubscribe();

  // 4. Notify backend to remove it
  return new Promise((resolve, reject) => {
    DBRequest({
      config,
      path: 'web-push/unsubscribe',
      method: API_POST(subscription), // send subscription info to delete on backend
      onResponse: (res) => {
        resolve('success');
      },
      onError: (error) => {
        reject('error');
      },
    });
  });
}
